import { useRef, useState } from "react";
import { X, Camera, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const INSPECTION_ANGLES = [
  "front", "rear", "driver_side", "passenger_side", "wheels",
  "interior", "dashboard", "odometer", "fuel_level",
];

function AngleSlot({ angleKey, url, onUpload, onClear }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpload(r.data.url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`inspection-slot-${angleKey}`} className="relative">
      {url ? (
        <div className="relative rounded-xl overflow-hidden border-2 border-green-500 aspect-[4/3]">
          <img src={url} alt={t(`inspection.angles.${angleKey}`)} className="w-full h-full object-cover" />
          <span className="absolute top-1.5 left-1.5 bg-green-600 text-white rounded-full p-0.5"><CheckCircle2 size={14} /></span>
          <button onClick={onClear} data-testid={`inspection-retake-${angleKey}`} className="absolute top-1.5 right-1.5 bg-slate-900/70 text-white rounded-full p-1 hover:bg-slate-900"><X size={12} /></button>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()} disabled={busy}
          className="w-full aspect-[4/3] rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 bg-slate-50 grid place-items-center transition-colors">
          {busy ? <Loader2 size={20} className="animate-spin text-blue-600" /> : <Camera size={20} className="text-slate-400" />}
        </button>
      )}
      <p className={`text-[11px] font-semibold mt-1.5 text-center ${url ? "text-green-700" : "text-slate-500"}`}>{t(`inspection.angles.${angleKey}`)}</p>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid={`inspection-input-${angleKey}`}
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}

// Guided 9-angle vehicle inspection for trip check-in / check-out.
// stage: customer_start | customer_return
export function InspectionDialog({ open, onClose, endpoint, stage, onDone }) {
  const { t } = useTranslation();
  const [angles, setAngles] = useState({});
  const [saving, setSaving] = useState(false);
  if (!open) return null;

  const done = INSPECTION_ANGLES.filter((k) => angles[k]).length;
  const complete = done === INSPECTION_ANGLES.length;

  const submit = async () => {
    if (!complete) { toast.error(t("inspection.needAll")); return; }
    setSaving(true);
    try {
      await api.post(endpoint, { stage, images: [], angles });
      toast.success(t("inspection.saved"));
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="inspection-dialog">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-blue-600 text-white"><Camera size={17} /></span>
            <div>
              <h2 className="font-heading text-lg font-bold text-slate-900">{t(`inspection.${stage}.title`)}</h2>
              <p className="text-xs text-slate-400">{t(`inspection.${stage}.hint`)}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="inspection-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl p-3.5 text-sm text-blue-800">
            <ShieldCheck size={16} className="mt-0.5 shrink-0" />
            <span>{t("inspection.protect")}</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {INSPECTION_ANGLES.map((k) => (
              <AngleSlot key={k} angleKey={k} url={angles[k]}
                onUpload={(url) => setAngles((p) => ({ ...p, [k]: url }))}
                onClear={() => setAngles((p) => { const n = { ...p }; delete n[k]; return n; })} />
            ))}
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className={`text-xs font-semibold ${complete ? "text-green-600" : "text-amber-600"}`} data-testid="inspection-count">
              {done} / {INSPECTION_ANGLES.length} {complete ? "✓" : t("inspection.photosRequired")}
            </span>
            <div className="flex gap-3">
              <button onClick={onClose} className="border-2 border-slate-200 px-6 py-2.5 rounded-full font-semibold text-slate-700 hover:bg-slate-50 transition-colors">{t("common.cancel")}</button>
              <button onClick={submit} disabled={saving || !complete} data-testid="inspection-submit"
                className="bg-blue-600 text-white px-7 py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-40 flex items-center gap-2">
                {saving ? <><Loader2 size={16} className="animate-spin" /> {t("tripPhotos.saving")}</> : t("inspection.submit")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
